import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getFullApiUrl, podcastId } from "../../config";
import { formatDateSlash, getToken, setPodcastSession } from "../../utils";
import { logout } from "../../features/auth/authSlice";
import { ExpiredPage } from "../ExpiredPage";
import { LoadingPage } from "../LoadingPage";
import { ErrorPage } from "../ErrorPage";
import AuthCard from "../auth/AuthCard";
import Notice from "../ui/Notice";
import { Badge, Button, Dialog, Field, Input } from "../../designSystem";

const ZsxqPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [zsxqMessage, setZsxqMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [memberName, setMemberName] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [bindings, setBindings] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [unbindTarget, setUnbindTarget] = useState(null); // 待解绑的 binding
  const [processing, setProcessing] = useState(false);
  const token = getToken() || "";

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleTokenExpired = useCallback(() => {
    setError("登录已过期，请重新登录。");
    setLoading(false);
    setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("podcast_session");
      dispatch(logout());
      navigate("/login");
    }, 2000);
  }, [dispatch, navigate]);

  /** 根据最新付费状态同步本地 podcast_session（解绑后可能失去会员权限）。 */
  const refreshPodcastSession = useCallback(async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/subscribe/user_payment_status"),
        authHeaders
      );
      if (response.data?.podcast_session) {
        setPodcastSession(response.data.podcast_session);
      } else {
        localStorage.removeItem("podcast_session");
      }
    } catch (err) {
      console.error("Failed to refresh payment status:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchBinding = useCallback(async () => {
    const response = await axios.get(
      getFullApiUrl("/api/v1/auth/zsxq_binding"),
      authHeaders
    );
    setBindings(response.data?.bindings || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        await axios.post(
          getFullApiUrl("/api/v1/auth/account_verified"),
          {},
          authHeaders
        );
        setIsVerified(true);
        await fetchBinding();
        setLoading(false);
      } catch (err) {
        if (err.response?.data?.error === "token has expired") {
          setExpired(true);
        } else if (err.response?.config?.url?.includes("account_verified")) {
          // 邮箱未验证时 account_verified 返回错误，仍展示验证引导
          setIsVerified(false);
          setLoading(false);
        } else {
          console.error("Error loading zsxq page:", err);
          setError("页面加载失败，请稍后重试。");
          setLoading(false);
        }
      }
    };

    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResendEmail = async () => {
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/send_email"),
        {},
        authHeaders
      );
      if (response.status === 200) {
        setIsDisabled(true);
        const interval = setInterval(() => {
          setCountdown((prevCountdown) => prevCountdown - 1);
        }, 1000);
        setTimeout(() => {
          clearInterval(interval);
          setIsDisabled(false);
          setCountdown(30);
        }, 30000);
        setZsxqMessage("验证邮件发送成功");
      } else {
        setZsxqMessage("验证邮件发送失败");
      }
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        handleTokenExpired();
      } else {
        console.error("Error resending email:", err);
        setZsxqMessage("验证邮件发送失败");
      }
    }
  };

  const handleMemberSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    if (memberName === "") {
      setZsxqMessage("用户昵称或成员编号错误");
      return;
    }
    const isnum = /^\d+$/.test(memberNumber);
    if (!isnum) {
      setZsxqMessage("用户昵称或成员编号错误");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/verify_zsxq"),
        {
          member_name: memberName,
          member_number: memberNumber,
          podcast_id: podcastId,
        },
        authHeaders
      );
      if (response.status === 200) {
        setPodcastSession(response.data.podcastSession);
        setZsxqMessage("");
        setSuccessMessage("绑定成功，该星球会员的时长已计入总有效期。");
        setMemberName("");
        setMemberNumber("");
        setShowAddForm(false);
        await fetchBinding();
      } else {
        console.error("Verify failed:", response.data);
        setZsxqMessage(response.data.error);
      }
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        handleTokenExpired();
      } else {
        console.error("Verify failed:", err.response?.data?.error);
        const apiError = err.response?.data?.error;
        if (apiError === "user already existed" && err.response?.data?.existing_email) {
          setZsxqMessage(
            `该会员信息已被账号 ${err.response.data.existing_email} 绑定。如需换绑，请先在原账号中解除绑定。`
          );
        } else if (apiError === "already bound to this account") {
          setZsxqMessage("该知识星球账号已绑定在当前账号下。");
        } else if (apiError === "invalid member information") {
          setZsxqMessage("会员信息无效，请核对用户昵称和成员编号。");
        } else {
          setZsxqMessage(apiError || "绑定失败，请稍后重试。");
        }
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleUnbind = async () => {
    if (!unbindTarget) return;
    setProcessing(true);
    try {
      await axios.post(
        getFullApiUrl("/api/v1/auth/unbind_zsxq"),
        { zsxqs_id: unbindTarget.zsxqs_id },
        authHeaders
      );
      setUnbindTarget(null);
      await refreshPodcastSession();
      await fetchBinding();
      setSuccessMessage("已解除绑定。该知识星球账号现在可以绑定到其他账号。");
      setZsxqMessage("");
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        handleTokenExpired();
      } else {
        console.error("Unbind failed:", err);
        setZsxqMessage(err.response?.data?.error || "解除绑定失败，请稍后重试。");
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (expired) {
    return <ExpiredPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (!isVerified) {
    return (
      <AuthCard
        title="知识星球会员通道"
        subtitle="请先完成邮箱验证，再绑定知识星球账号。"
      >
        <div style={{ display: "grid", gap: 12 }}>
          <Notice variant="warning">
            请检查注册邮箱，点击邮件中的链接完成验证；验证后刷新本页面即可绑定。
          </Notice>
          <Notice variant="info">
            如未收到验证邮件，请检查邮箱的垃圾邮件文件夹。
          </Notice>
          {zsxqMessage ? (
            <Notice
              variant={zsxqMessage === "验证邮件发送成功" ? "success" : "error"}
            >
              {zsxqMessage}
            </Notice>
          ) : null}
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={handleResendEmail}
            disabled={isDisabled}
          >
            {isDisabled
              ? `重新发送验证邮件（${countdown}s）`
              : "重新发送验证邮件"}
          </Button>
        </div>
      </AuthCard>
    );
  }

  // 已有绑定且未在添加流程中：显示绑定列表与管理入口
  if (bindings.length > 0 && !showAddForm) {
    return (
      <AuthCard
        title="知识星球绑定信息"
        subtitle={`当前账号已绑定 ${bindings.length} 个知识星球会员，各自的会员时长叠加计入总有效期。`}
      >
        <div style={{ display: "grid", gap: 12 }}>
          {successMessage ? (
            <Notice variant="success">{successMessage}</Notice>
          ) : null}
          {bindings.map((b) => {
            const active = b.membership_end_at
              ? new Date(b.membership_end_at) > new Date()
              : false;
            return (
              <div className="lt-card" key={b.zsxqs_id}>
                <div className="lt-set-row" style={{ flexWrap: "wrap" }}>
                  <span className="t" style={{ flex: "1 1 220px" }}>
                    <b>
                      {b.zsxq_name}
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontWeight: 400,
                          color: "var(--text-3)",
                          marginLeft: 8,
                          fontSize: "var(--text-sm)",
                        }}
                      >
                        #{b.zsxq_number}
                      </span>
                    </b>
                    <p>
                      星球会员有效期至 {formatDateSlash(b.membership_end_at)} ·
                      绑定于 {formatDateSlash(b.bound_at)}
                    </p>
                  </span>
                  <span className="r">
                    {active ? (
                      <Badge variant="member" icon="verified">
                        生效中
                      </Badge>
                    ) : (
                      <Badge variant="neutral" icon={null}>
                        已过期
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUnbindTarget(b)}
                    >
                      解除绑定
                    </Button>
                  </span>
                </div>
              </div>
            );
          })}
          <Notice variant="info">
            所有渠道（多个知识星球、Stripe
            订阅等）的会员时长叠加计算：重叠的部分会顺延到总有效期末尾，不会浪费。
          </Notice>
          {zsxqMessage ? <Notice variant="error">{zsxqMessage}</Notice> : null}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px", display: "flex" }}>
              <Button
                size="lg"
                fullWidth
                icon="add"
                onClick={() => {
                  setSuccessMessage("");
                  setZsxqMessage("");
                  setShowAddForm(true);
                }}
              >
                绑定另一个星球账号
              </Button>
            </div>
            <div style={{ flex: "1 1 160px", display: "flex" }}>
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={() => navigate("/subscription/manage")}
              >
                查看总有效期
              </Button>
            </div>
          </div>
        </div>

        <Dialog
          open={!!unbindTarget}
          title="确认解除绑定？"
          onClose={() => setUnbindTarget(null)}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setUnbindTarget(null)}
                disabled={processing}
              >
                取消
              </Button>
              <Button variant="danger" loading={processing} onClick={handleUnbind}>
                {processing ? "正在处理" : "确认解除"}
              </Button>
            </>
          }
        >
          <p style={{ color: "var(--text-2)", fontSize: "var(--text-sm)" }}>
            解除与「{unbindTarget?.zsxq_name}（#{unbindTarget?.zsxq_number}）」
            的绑定后，本账号将不再计入该星球会员的时长；该知识星球账号可以重新绑定到其他账号。
          </p>
        </Dialog>
      </AuthCard>
    );
  }

  // 未绑定，或在"绑定另一个账号"流程中：显示绑定表单
  return (
    <AuthCard
      title="知识星球会员通道"
      subtitle="填写知识星球的用户昵称和成员编号，绑定后即可收听付费节目。"
    >
      <form onSubmit={handleMemberSubmit} noValidate style={{ display: "grid", gap: 16 }}>
        {successMessage ? (
          <Notice variant="success">{successMessage}</Notice>
        ) : null}
        <Field label="会员昵称" htmlFor="member_name">
          <Input
            id="member_name"
            name="member_name"
            icon="person"
            autoFocus
            value={memberName}
            onChange={(e) => setMemberName(e.target.value)}
          />
        </Field>
        <Field
          label="成员编号"
          htmlFor="member_number"
          hint="在知识星球「更多」页面头像下方可以找到"
        >
          <Input
            id="member_number"
            name="member_number"
            icon="tag"
            inputMode="numeric"
            value={memberNumber}
            onChange={(e) => setMemberNumber(e.target.value)}
          />
        </Field>
        <Notice variant="info">
          可以绑定多个知识星球账号，也可以与其他渠道（如 Stripe
          订阅）同时持有，所有会员时长叠加计算。
        </Notice>
        <Notice variant="error">{zsxqMessage}</Notice>
        <Button type="submit" size="lg" fullWidth loading={processing}>
          {processing ? "正在绑定" : "绑定"}
        </Button>
        {bindings.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon="arrow_back"
            style={{ justifySelf: "center" }}
            onClick={() => {
              setZsxqMessage("");
              setShowAddForm(false);
            }}
          >
            返回绑定列表
          </Button>
        ) : null}
      </form>
    </AuthCard>
  );
};

export default ZsxqPage;
